import { Paper } from "helpers/api";
import { coerceDateUTC } from "helpers/date";

/**
 * Utility functions for timeline beeswarm plot data preparation
 */

/**
 * Get effective publication date for a paper
 * Uses publish_date if available, otherwise falls back to year
 */
export function getEffectiveDate(paper: Paper): Date {
  if (paper.publish_date) {
    const date = coerceDateUTC(paper.publish_date);
    if (date) return date;
  }
  // Fallback to middle of year if only year is available
  return new Date(Date.UTC(paper.year, 6, 1)); // July 1st
}

/**
 * Prepare papers data for timeline visualization
 * Filters and sorts papers by publication date
 */
export function preparePapersForTimeline(papers: Paper[]): Paper[] {
  if (!papers.length) return [];

  // Filter papers with valid years
  const validPapers = papers.filter((paper) => paper.year > 0);

  // Sort by publication date (ascending) for timeline
  return validPapers.sort((a, b) => {
    const dateA = getEffectiveDate(a);
    const dateB = getEffectiveDate(b);
    return dateA.getTime() - dateB.getTime();
  });
}

/**
 * Get date range statistics for timeline scaling
 */
export function getDateRange(papers: Paper[]): {
  min: Date;
  max: Date;
  span: number; // in days
} {
  if (!papers.length) {
    const now = new Date();
    return { min: now, max: now, span: 0 };
  }

  const validPapers = papers.filter((paper) => paper.year > 0);
  if (!validPapers.length) {
    const now = new Date();
    return { min: now, max: now, span: 0 };
  }

  const dates = validPapers.map(getEffectiveDate);
  const min = new Date(Math.min(...dates.map((d) => d.getTime())));
  const max = new Date(Math.max(...dates.map((d) => d.getTime())));
  const span = Math.max(
    1,
    (max.getTime() - min.getTime()) / (1000 * 60 * 60 * 24)
  ); // days

  return { min, max, span };
}

/**
 * Get year range for papers
 */
export function getYearRange(papers: Paper[]): { min: number; max: number } {
  if (!papers.length) {
    const currentYear = new Date().getFullYear();
    return { min: currentYear, max: currentYear };
  }

  const years = papers
    .map((p) => p.year)
    .filter((year) => year > 0)
    .sort((a, b) => a - b);

  if (!years.length) {
    const currentYear = new Date().getFullYear();
    return { min: currentYear, max: currentYear };
  }

  return { min: years[0], max: years[years.length - 1] };
}

/**
 * Calculate citation statistics for papers
 */
export function getCitationStats(papers: Paper[]): {
  min: number;
  max: number;
  range: number;
} {
  if (!papers.length) return { min: 0, max: 0, range: 0 };

  const citationCounts = papers.map((paper) => paper.citation_count || 0);
  const min = Math.min(...citationCounts);
  const max = Math.max(...citationCounts);
  const range = max - min;

  return { min, max, range };
}

/**
 * Calculate scale factor for a paper based on its citation count
 * Returns a value between 1x (minimum citations) and 2x (maximum citations)
 */
export function getCitationScale(
  citationCount: number,
  citationStats: { min: number; max: number; range: number }
): number {
  if (citationStats.range === 0) return 1; // All papers have same citation count

  const normalizedCount =
    (citationCount - citationStats.min) / citationStats.range;

  // Scale from 1x (base) to 2x (max) based on citation count
  return 1 + normalizedCount;
}

/**
 * Determine if timeline should use yearly or monthly granularity
 * Uses yearly granularity if span > cutoff years, else monthly
 */
export function shouldUseYearlyGranularity(
  papers: Paper[],
  yearGranularityCutoff: number = 7
): boolean {
  if (!papers.length) return true;

  const dateRange = getDateRange(papers);
  const yearsSpan =
    (dateRange.max.getTime() - dateRange.min.getTime()) /
    (1000 * 60 * 60 * 24 * 365);

  return yearsSpan > yearGranularityCutoff;
}

/**
 * Group papers by time periods (yearly or monthly)
 * Returns a map where keys are formatted time periods and values are arrays of papers
 */
export function groupPapersByTimePeriod(
  papers: Paper[],
  yearGranularityCutoff: number = 7
): Map<string, Paper[]> {
  const validPapers = preparePapersForTimeline(papers);
  const useYearly = shouldUseYearlyGranularity(
    validPapers,
    yearGranularityCutoff
  );
  const groupedPapers = new Map<string, Paper[]>();

  validPapers.forEach((paper) => {
    const date = getEffectiveDate(paper);
    let timeKey: string;

    if (useYearly) {
      timeKey = date.getFullYear().toString();
    } else {
      // Monthly format: "YYYY-MM" or "MMM YYYY" for better readability
      const year = date.getFullYear();
      const month = date.getMonth();
      const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      timeKey = `${monthNames[month]} ${year}`;
    }

    if (!groupedPapers.has(timeKey)) {
      groupedPapers.set(timeKey, []);
    }
    groupedPapers.get(timeKey)!.push(paper);
  });

  return groupedPapers;
}

/**
 * Add buffer to date range for better visualization
 * Adds 1 year buffer for yearly granularity, 2 months for monthly granularity
 */
export function addBufferToDateRange(
  dateRange: { min: Date; max: Date },
  yearGranularityCutoff: number = 7
): { min: Date; max: Date } {
  const yearsSpan =
    (dateRange.max.getTime() - dateRange.min.getTime()) /
    (1000 * 60 * 60 * 24 * 365);

  if (yearsSpan > yearGranularityCutoff) {
    // Yearly ticks - add 1 year buffer
    return {
      min: new Date(dateRange.min.getFullYear() - 1, 0, 1),
      max: new Date(dateRange.max.getFullYear() + 1, 11, 31),
    };
  } else {
    // Monthly ticks - add 2 months buffer
    return {
      min: new Date(
        dateRange.min.getFullYear(),
        dateRange.min.getMonth() - 2,
        1
      ),
      max: new Date(
        dateRange.max.getFullYear(),
        dateRange.max.getMonth() + 2,
        0
      ),
    };
  }
}
